import { useMemo, useCallback, useState } from 'react';

/**
 * Custom React hook for dynamically loading and managing the Razorpay Checkout script.
 *
 * This hook ensures the Razorpay script is loaded into the browser, allows lazy initialization,
 * and provides access to the `Razorpay` instance when ready.
 *
 * @returns {Object} An object with utility methods and state:
 * @returns {Function} loadScript - Loads the Razorpay script dynamically. Returns a Promise.
 * @returns {Function} getRazorpayInstance - Returns a new Razorpay instance with the given options.
 * @returns {boolean} isScriptLoaded - Whether the Razorpay script is loaded and ready.
 *
 * @example
 * const { loadScript, getRazorpayInstance, isScriptLoaded } = useRazorpay();
 *
 * useEffect(() => {
 *   loadScript().then(() => {
 *     const razorpay = getRazorpayInstance({
 *       key: 'your_key_here',
 *       amount: 1000,
 *       currency: 'INR',
 *       name: 'Your Company',
 *       handler: (response) => console.log(response),
 *     });
 *     razorpay.open();
 *   });
 * }, []);
 */
export const useRazorpay = () => {
  const RAZORPAY_SCRIPT_URL = 'https://checkout.razorpay.com/v1/checkout.js';
  const isClient = useMemo(() => typeof window !== 'undefined', []);
  const [isScriptLoaded, setIsScriptLoaded] = useState(false);

  /**
   * Checks if the Razorpay script is already loaded in the window.
   *
   * @returns {boolean} True if `window.Razorpay` is available.
   */
  const checkScriptLoaded = useCallback(() => {
    return isClient && typeof window.Razorpay === 'function';
  }, [isClient]);

  /**
   * Dynamically loads the Razorpay Checkout script if not already present.
   *
   * @returns {Promise<void>} Resolves when script is loaded, rejects on error or if not in a browser.
   */
  const loadScript = useCallback(() => {
    return new Promise((resolve, reject) => {
      if (!isClient) return reject('Not in a browser');

      if (checkScriptLoaded()) {
        setIsScriptLoaded(true);
        resolve();
        return;
      }

      const existingScript = document.querySelector(
        `script[src="${RAZORPAY_SCRIPT_URL}"]`,
      );
      if (existingScript) {
        existingScript.onload = () => {
          setIsScriptLoaded(true);
          resolve();
        };
        existingScript.onerror = reject;
        return;
      }

      const script = document.createElement('script');
      script.src = RAZORPAY_SCRIPT_URL;
      script.onload = () => {
        setIsScriptLoaded(true);
        resolve();
      };
      script.onerror = reject;
      document.body.appendChild(script);
    });
  }, [isClient, checkScriptLoaded]);

  /**
   * Returns a new Razorpay instance using the given options.
   * Throws an error if the script has not yet been loaded.
   *
   * @param {Object} options - Razorpay checkout options (key, amount, handler, etc.)
   * @returns {Razorpay} A new Razorpay instance ready to be opened.
   * @throws {Error} If Razorpay script is not loaded yet.
   */
  const getRazorpayInstance = (options) => {
    if (!checkScriptLoaded()) {
      throw new Error('Razorpay script not loaded yet');
    }
    return new window.Razorpay(options);
  };

  return { getRazorpayInstance, loadScript, isScriptLoaded };
};
